// src/components/ConversionCTAs.jsx
// Reusable conversion CTA components for cancer pages

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { trackCTA } from '@/utils/seo/analytics';
import { 
  MessageCircle, 
  Calculator, 
  Stethoscope, 
  Building2,
  ArrowRight,
  Phone
} from 'lucide-react';

/**
 * WhatsApp CTA Button
 */
export function WhatsAppCTA({ className = "", text = "Chat on WhatsApp", location = "unknown" }) {
  const currentLocation = useLocation();
  const whatsappNumber = "+919022792824"; // PraesidioCare verified support line
  const whatsappMessage = encodeURIComponent("Hi, I need help with cancer treatment options.");
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${whatsappMessage}`;

  const handleClick = () => {
    trackCTA('whatsapp', location, {
      page: currentLocation.pathname,
      button_text: text
    });
  };

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={handleClick}
    >
      <Button className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-cream-paper">
        <MessageCircle className="h-4 w-4 mr-2" />
        {text}
      </Button>
    </a>
  );
}

/**
 * Get Matched CTA Button - Links to /get-started
 */
export function GetMatchedCTA({ className = "", text = "Get Matched", location = "unknown" }) {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleClick = () => {
    trackCTA('get_matched', location, {
      page: currentLocation.pathname,
      button_text: text
    });
    navigate('/get-started');
  };

  return (
    <Button
      onClick={handleClick}
      className={`w-full bg-forest-ink hover:bg-forest-shadow text-white ${className}`}
    >
      <Building2 className="h-4 w-4 mr-2" />
      {text}
      <ArrowRight className="h-4 w-4 ml-2" />
    </Button>
  );
}

/**
 * Find Hospitals CTA Button - Links to /find-hospitals
 */
export function FindHospitalsCTA({ className = "", text = "Find Hospitals", location = "unknown" }) {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleClick = () => {
    trackCTA('find_hospitals', location, {
      page: currentLocation.pathname,
      button_text: text
    });
    navigate('/find-hospitals');
  };

  return (
    <Button
      onClick={handleClick}
      className={`w-full bg-forest-ink hover:bg-forest-shadow text-white ${className}`}
    >
      <Building2 className="h-4 w-4 mr-2" />
      {text}
      <ArrowRight className="h-4 w-4 ml-2" />
    </Button>
  );
}

/**
 * Cost Calculator CTA Button - Links to /cost-calculator
 */
export function CostCalculatorCTA({ className = "", text = "Estimate Cost", location = "unknown" }) {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleClick = () => {
    trackCTA('estimate_cost', location, {
      page: currentLocation.pathname,
      button_text: text
    });
    navigate('/cost-calculator');
  };

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      className={`w-full border-slate-300 bg-white text-slate-800 hover:bg-emerald-50/80 hover:text-forest-ink hover:border-emerald-600/40 font-medium transition shadow-xs ${className}`}
    >
      <Calculator className="h-4 w-4 mr-2 text-emerald-700" />
      {text}
    </Button>
  );
}

/**
 * Marketing CTA → Rapid Second Opinion surface.
 */
export function SecondOpinionCTA({ className = "", text = "Get Second Opinion", location = "unknown" }) {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleClick = () => {
    trackCTA('second_opinion', location, {
      page: currentLocation.pathname,
      button_text: text
    });
    navigate('/second-opinion');
  };

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      className={`w-full border-slate-300 bg-white text-slate-800 hover:bg-emerald-50/80 hover:text-forest-ink hover:border-emerald-600/40 font-medium transition shadow-xs ${className}`}
    >
      <Stethoscope className="h-4 w-4 mr-2 text-emerald-700" />
      {text}
    </Button>
  );
}

/**
 * Sticky CTA Bar (for mobile/desktop sticky positioning)
 */
export function StickyCTABar({ cancerType = "cancer" }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-slate-200/80 p-3 sm:p-4 md:hidden shadow-lg">
      <div className="max-w-7xl mx-auto flex gap-2 sm:gap-3">
        <GetMatchedCTA className="flex-1 text-xs sm:text-sm py-2" text="Get Matched" location="sticky_bar" />
        <WhatsAppCTA className="flex-1 text-xs sm:text-sm py-2" text="Chat" location="sticky_bar" />
      </div>
    </div>
  );
}

/**
 * Conversion CTA Card (for inline placement)
 */
export function ConversionCTACard({ cancerType = "cancer" }) {
  return (
    <Card className="bg-gradient-to-br from-emerald-50/80 via-teal-50/40 to-white border border-emerald-200/80 rounded-2xl shadow-sm">
      <CardContent className="p-6 sm:p-8 space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 text-emerald-900 text-xs font-semibold">
          <Stethoscope className="w-3.5 h-3.5 text-emerald-700" />
          Dedicated Oncology Guidance
        </div>
        <h3 className="text-xl sm:text-2xl font-bold font-serif text-forest-ink tracking-tight break-words">
          Get Matched Care & Treatment Guidance for {cancerType}
        </h3>
        <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl">
          Connect with ByOnco to compare verified centers, calculate transparent cost estimates, and access independent multidisciplinary second opinions.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
          <FindHospitalsCTA text="Find Hospitals" location="inline_card" />
          <WhatsAppCTA text="24/7 Care Chat" location="inline_card" />
          <CostCalculatorCTA text="Estimate Cost" location="inline_card" />
          <SecondOpinionCTA text="Second Opinion" location="inline_card" />
        </div>
      </CardContent>
    </Card>
  );
}
